import { Button } from '@/shared/ui/button';
import { Package, Settings, Globe, FileText, Type, Text } from 'lucide-react';
import { getOverageInfo } from "@/shared/utils/serviceLineCalculations";
import { ServiceLine } from "@/shared/types/serviceLineTypes";
import { ServiceLineStatus } from "@/shared/types/serviceLineTypes";
import { useState } from 'react';
import { OverageSettingsDialog } from '@/domains/service-lines/dialogs/OverageSettingsDialog';
import { PublicIpDialog } from '@/domains/service-lines/dialogs/PublicIpDialog';
import RenameServiceLineDialog from '@/domains/service-lines/dialogs/RenameServiceLineDialog';
import SubscriptionsDialog from '@/domains/service-lines/dialogs/SubscriptionsDialog';
import { ServiceLineTypeDialog } from '@/domains/service-lines/dialogs/ServiceLineTypeDialog';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import { Organization } from '@/shared/types/userTypes';
import { getOverageStatusTextColorClass, getServiceLineTypeTextColorClass } from '@/shared/utils/color-utils-service-lines';
import { type AppShellMode } from '@/app/shared/appShellMode';
import { OrgType } from '@/shared/types/userRoleTypes';
import { usePermissions } from '@/shared/hooks/use-permissions';
import ServiceLineNotesDialog from '../dialogs/ServiceLineNotesDialog';

interface ServiceLineDetailsSectionProps {
  mode: AppShellMode;
  serviceLine: ServiceLine;
  organization?: Organization;
  refetchServiceLine?: () => void;
}

export const ServiceLineDetailsSection = ({
  mode,
  serviceLine,
  organization,
  refetchServiceLine,
}: ServiceLineDetailsSectionProps) => {
  const { hasPermission } = usePermissions();
  const canManageServiceLine = serviceLine.status !== ServiceLineStatus.SUSPENDED && hasPermission(mode === 'tenant' ? OrgType.TENANT : OrgType.CUSTOMER, Feature.SERVICE_LINE, Permission.MANAGE);
  const canManageServiceLineSubscriptions = serviceLine.status !== ServiceLineStatus.SUSPENDED && hasPermission(mode === 'tenant' ? OrgType.TENANT : OrgType.CUSTOMER, Feature.SERVICE_LINE_SUBSCRIPTIONS, Permission.MANAGE);

  const [isOverageDialogOpen, setIsOverageDialogOpen] = useState(false);
  const [isPublicIpDialogOpen, setIsPublicIpDialogOpen] = useState(false);
  const [isRenameServiceLineDialogOpen, setIsRenameServiceLineDialogOpen] = useState(false);
  const [isSubscriptionsDialogOpen, setIsSubscriptionsDialogOpen] = useState(false);
  const [isServiceLineTypeDialogOpen, setIsServiceLineTypeDialogOpen] = useState(false);
  const [isServiceLineNotesDialogOpen, setIsServiceLineNotesDialogOpen] = useState(false);

  const overageInfo = getOverageInfo(serviceLine?.overage);

  return (
    <div className="grid lg:grid-cols-2 grid-cols-1 gap-4">
      {/* Nickname */}
      <div className="glass-panel flex justify-between items-center p-4 rounded-lg border border">
        <div className="flex items-center gap-4">
          <Text className="h-5 w-5" />
          <div>
            <p className="text-sm font-medium">Nick Name</p>
            <p className="text-base font-bold">
              {serviceLine.serviceLineName || serviceLine.product?.orbiusProductName || 'Unnamed Service Line'}
            </p>
          </div>
        </div>
        {canManageServiceLine && (
          <Button
            variant="outline"
            size="default"
            className="gap-2"
            onClick={() => setIsRenameServiceLineDialogOpen(true)}
            disabled={serviceLine.updateAllowed === false || serviceLine.status === ServiceLineStatus.SUSPENDED || serviceLine.status === ServiceLineStatus.PAUSED}
          >
            Manage
          </Button>
        )}
      </div>

      {/* Subscription plan */}
      <div className="glass-panel flex justify-between items-center p-4 rounded-lg border border">
        <div className="flex items-center gap-4">
          <Package className="h-5 w-5" />
          <div>
            <p className="text-sm font-medium">Subscription Plan</p>
            <p className="text-base font-bold">{serviceLine.product.orbiusProductName}</p>
          </div>
        </div>
        {canManageServiceLineSubscriptions && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => setIsSubscriptionsDialogOpen(true)}
            disabled={serviceLine.updateAllowed === false || serviceLine.status === ServiceLineStatus.SUSPENDED || serviceLine.status === ServiceLineStatus.PAUSED}>
            Manage
          </Button>
        )}
      </div>

      {/* Service Line Type */}
      <div className="glass-panel flex justify-between items-center p-4 rounded-lg border border">
        <div className="flex items-center gap-4">
          <Type className="h-5 w-5" />
          <div>
            <p className="text-sm font-medium">Service Line Type</p>
            <p className={`text-base font-bold ${getServiceLineTypeTextColorClass(serviceLine.type)}`}>
              {serviceLine?.type ? serviceLine?.type?.toString() : 'Not Set'}
            </p>
          </div>
        </div>
        {canManageServiceLine && (
          <Button
            variant="outline"
            size="default"
            className="gap-2"
            onClick={() => setIsServiceLineTypeDialogOpen(true)}
            disabled={serviceLine.updateAllowed === false}
          >
            Manage
          </Button>
        )}
      </div>

      {/* Overage Management */}
      {serviceLine.overageAllowed === true && (
        <div className="glass-panel flex justify-between items-center p-4 rounded-lg border border">
          <div className="flex items-center gap-4">
            <Settings className="h-5 w-5" />
            <div>
              <p className="text-sm font-medium">Overage Info</p>
              <p className={`text-base font-bold ${getOverageStatusTextColorClass(overageInfo.status)}`}>
                {overageInfo.info}
              </p>
            </div>
          </div>
          {canManageServiceLine && (
            <Button
              variant="outline"
              size="default"
              className="gap-2"
              onClick={() => setIsOverageDialogOpen(true)}
              disabled={serviceLine.updateAllowed === false || serviceLine.status === ServiceLineStatus.SUSPENDED || serviceLine.status === ServiceLineStatus.PAUSED}
            >
              Manage
            </Button>
          )}
        </div>
      )}

      {/* Public IP */}
      {serviceLine.product?.l2vpnSupported != null && serviceLine.product?.l2vpnSupported === false && (
        <div className="glass-panel flex justify-between items-center p-4 rounded-lg border border">
          <div className="flex items-center gap-4">
            <Globe className="h-5 w-5" />
            <div>
              <p className="text-sm font-medium">Public IP</p>
              <p className="text-base font-bold">
                {serviceLine.publicIp === true ? 'Enabled' : serviceLine.publicIp === false ? 'Disabled' : 'Not Set'}
              </p>
            </div>
          </div>
          {canManageServiceLine && (
            <Button
              variant="outline"
              size="default"
              className="gap-2"
              onClick={() => setIsPublicIpDialogOpen(true)}
              disabled={serviceLine.updateAllowed === false}
            >
              Manage
            </Button>
          )}
        </div>
      )}

      {/* Notes */}
      <div className="glass-panel flex justify-between items-center p-4 rounded-lg border border">
        <div className="flex items-center gap-4">
          <FileText className="h-5 w-5" />
          <div>
            <p className="text-sm font-medium">Service Line Notes</p>
            <p className="text-base font-bold text-wrap">
              {serviceLine.notes || 'Not Set'}
            </p>
          </div>
        </div>
        {canManageServiceLine && (
          <Button
            variant="outline"
            size="default"
            className="gap-2"
            onClick={() => setIsServiceLineNotesDialogOpen(true)}
            disabled={serviceLine.updateAllowed === false}
          >
            Manage
          </Button>
        )}
      </div>

      <>
        {/* Overage Settings Dialog */}
        <OverageSettingsDialog
          open={isOverageDialogOpen}
          onOpenChange={setIsOverageDialogOpen}
          onSuccess={refetchServiceLine}
          organization={organization}
          serviceLine={serviceLine}
        />

        {/* Public IP Dialog */}
        <PublicIpDialog
          open={isPublicIpDialogOpen}
          onOpenChange={setIsPublicIpDialogOpen}
          onSuccess={refetchServiceLine}
          serviceLine={serviceLine}
        />

        {/* Rename Service Line Dialog */}
        <RenameServiceLineDialog
          open={isRenameServiceLineDialogOpen}
          onOpenChange={setIsRenameServiceLineDialogOpen}
          onSuccess={refetchServiceLine}
          serviceLine={serviceLine}
        />

        {/* Subscriptions Dialog */}
        <SubscriptionsDialog
          open={isSubscriptionsDialogOpen}
          onOpenChange={setIsSubscriptionsDialogOpen}
          onSuccess={refetchServiceLine}
          serviceLine={serviceLine}
        />

        {/* Service Line Type Dialog */}
        <ServiceLineTypeDialog
          open={isServiceLineTypeDialogOpen}
          onOpenChange={setIsServiceLineTypeDialogOpen}
          onSuccess={refetchServiceLine}
          serviceLine={serviceLine}
        />

        {/* Service Line Notes Dialog */}
        <ServiceLineNotesDialog
          open={isServiceLineNotesDialogOpen}
          onOpenChange={setIsServiceLineNotesDialogOpen}
          onSuccess={refetchServiceLine}
          serviceLine={serviceLine}
        />
      </>
    </div >
  );
}; 